/* ---------------------------------------------------------------------------
   Tiempo relativo para el listado de informes: "hace 2 h", "ayer", "En 3 días".

   Funciones puras con `ahora` inyectable, para probarlas sin congelar el reloj.
   Los días se cuentan de CALENDARIO en Chile, no en franjas de 24 horas: algo
   de ayer a las 23:00 es "ayer" para quien lo lee, aunque hayan pasado dos horas.
--------------------------------------------------------------------------- */

const MINUTO = 60_000;
const HORA = 60 * MINUTO;

const DIA_CHILENO = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Santiago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Días enteros desde el 1970 del día de calendario chileno que contiene `fecha`. */
function diaChileno(fecha: Date): number {
  const [anio, mes, dia] = DIA_CHILENO.format(fecha).split("-").map(Number);

  return Math.round(Date.UTC(anio!, mes! - 1, dia!) / 86_400_000);
}

const plural = (n: number, singular: string, plural_: string) =>
  `hace ${n} ${n === 1 ? singular : plural_}`;

/**
 * Cuánto hace que ocurrió `iso`: minutos, horas (hasta seis), "hoy", "ayer",
 * días (hasta seis), semanas (hasta cuatro) y meses. Vacío si la fecha no es
 * válida; una fecha futura se lee como "hoy".
 */
export function haceCuanto(iso: string, ahora: Date = new Date()): string {
  const momento = new Date(iso);
  if (Number.isNaN(momento.getTime())) return "";

  const transcurrido = ahora.getTime() - momento.getTime();
  if (transcurrido < 0) return "hoy";

  if (transcurrido < HORA) return `hace ${Math.max(1, Math.floor(transcurrido / MINUTO))} min`;
  if (transcurrido < 6 * HORA) return `hace ${Math.floor(transcurrido / HORA)} h`;

  const dias = diaChileno(ahora) - diaChileno(momento);
  if (dias <= 0) return "hoy";
  if (dias === 1) return "ayer";
  if (dias < 7) return `hace ${dias} días`;
  if (dias < 30) return plural(Math.floor(dias / 7), "semana", "semanas");

  return plural(Math.floor(dias / 30), "mes", "meses");
}

export type TonoDeEntrega = "danger" | "warning" | "medio" | "success";

/**
 * La línea bajo la fecha de entrega: cuánto falta o cuánto pasó. `dias` es lo que
 * calcula el servidor (negativo = ya pasó). Un informe enviado dice "Entregado".
 * Sin fecha no hay nada que decir (`null`).
 */
export function describirEntregaRelativa(
  dias: number | null,
  enviado: boolean,
): { texto: string; tono: TonoDeEntrega } | null {
  if (enviado) return { texto: "Entregado", tono: "success" };
  if (dias === null) return null;

  if (dias < 0) {
    const pasados = -dias;
    return { texto: `Hace ${pasados} ${pasados === 1 ? "día" : "días"}`, tono: "danger" };
  }
  if (dias === 0) return { texto: "Hoy", tono: "warning" };
  if (dias === 1) return { texto: "Mañana", tono: "warning" };

  return { texto: `En ${dias} días`, tono: dias <= 3 ? "warning" : "medio" };
}
